/**
 * לקוח ה-API ללקוחות + כרטיס לקוח + מחירים מיוחדים — TASKS 5.4.
 */
import type { AdminApiError } from './adminApi.js';

export interface Customer {
  id: number;
  full_name: string;
  phone_e164: string;
  phone_raw: string | null;
  email: string | null;
  company: string | null;
  is_regular: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerListItem extends Customer {
  quote_count: number;
}

export interface CustomerPrice {
  id: number;
  product_id: number;
  variant_id: number | null;
  fixed_price: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  product_sku: string;
  product_name: string;
  product_base_price: string;
  variant_sku: string | null;
  variant_label: string | null;
  variant_price: string | null;
}

export interface QuoteSummary {
  id: number;
  quote_number: string;
  subtotal: string;
  vat_amount: string;
  total: string;
  price_basis: 'base' | 'customer' | 'manual';
  source: 'site' | 'admin';
  created_at: string;
}

async function parseErrorBody(res: Response): Promise<AdminApiError> {
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    return {
      status: res.status,
      code: body.error?.code ?? 'UNKNOWN_ERROR',
      message: body.error?.message ?? 'אירעה שגיאה לא צפויה.',
    };
  } catch {
    return { status: res.status, code: 'UNKNOWN_ERROR', message: 'אירעה שגיאה לא צפויה.' };
  }
}

export interface CustomerListParams {
  page?: number;
  isRegular?: boolean;
  q?: string;
}

export async function listCustomers(
  params: CustomerListParams = {},
): Promise<{ customers: CustomerListItem[]; page: number; pageSize: number; total: number }> {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.isRegular !== undefined) search.set('isRegular', String(params.isRegular));
  if (params.q) search.set('q', params.q);

  const res = await fetch(`/api/admin/customers?${search.toString()}`, {
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as {
    customers: CustomerListItem[];
    page: number;
    pageSize: number;
    total: number;
  };
}

export async function getCustomer(
  id: number,
): Promise<{ customer: Customer; prices: CustomerPrice[]; quotes: QuoteSummary[] }> {
  const res = await fetch(`/api/admin/customers/${id}`, { credentials: 'same-origin' });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as {
    customer: Customer;
    prices: CustomerPrice[];
    quotes: QuoteSummary[];
  };
}

export interface CustomerInput {
  fullName: string;
  phone: string;
  email?: string;
  company?: string;
  notes?: string;
  isRegular?: boolean;
}

export async function createCustomer(input: CustomerInput): Promise<{ customer: Customer }> {
  const res = await fetch('/api/admin/customers', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { customer: Customer };
}

export interface CustomerUpdateInput {
  fullName?: string;
  phone?: string;
  email?: string | null;
  company?: string | null;
  notes?: string | null;
  isRegular?: boolean;
}

export async function updateCustomer(
  id: number,
  input: CustomerUpdateInput,
): Promise<{ customer: Customer }> {
  const res = await fetch(`/api/admin/customers/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { customer: Customer };
}

export async function deleteCustomer(id: number): Promise<{ mode: 'deleted' | 'anonymized' }> {
  const res = await fetch(`/api/admin/customers/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { mode: 'deleted' | 'anonymized' };
}

export async function addCustomerAsRegularFromQuote(
  quoteId: number,
): Promise<{ customer: Customer }> {
  const res = await fetch(`/api/admin/customers/from-quote/${quoteId}`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { customer: Customer };
}

export interface CustomerPriceInput {
  productId: number;
  variantId?: number | null;
  fixedPrice: number;
}

export async function upsertCustomerPrice(
  customerId: number,
  input: CustomerPriceInput,
): Promise<{ price: CustomerPrice }> {
  const res = await fetch(`/api/admin/customers/${customerId}/prices`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { price: CustomerPrice };
}

export async function deleteCustomerPrice(customerId: number, priceId: number): Promise<void> {
  const res = await fetch(`/api/admin/customers/${customerId}/prices/${priceId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
}
